// Admin page uses this list in dropdown menu in Prize Management tool.
import BallMatchIndicator from 'components/landing/PrizesSection/BallMatchIndicator';
import { ReactNode } from 'react';
import { ASSET_PREFIX, colors } from 'utils/constants';

// Used in dropdown on prize management admin page
export const displayNameForValue = (value: number) => {
  switch (value) {
    case 5:
    case 10:
      return `£${value.toLocaleString()}`;

    default: {
      const prizeTier = getPrizeTierByValue(value);
      return `£${value.toLocaleString()} (${prizeTier?.title ?? 'unused tier'})`;
    }
  }
};

export const LottoPrizeValues = [
  100000, // unreleased
  25000,
  10000,
  2500, // old
  2000,
  500, // old
  300,
  100, // old
  50,
  25,
  10, // old
  5, // old
];

// Prizes page uses this list as the possible filter options
export const LottoPrizeValueFilterOptions = LottoPrizeValues.filter((value) => {
  // Filter out the old/unreleased prize values.
  return ![2500, 500, 100, 10, 5].includes(value);
});

export interface IPrizeTier {
  key: string;
  color: string;
  title: string;
  titleWithSuffix: ReactNode;
  subtitle?: string;
  badge: string;
  examplePrizes: string;
  value: number;
  poundSigns: {
    solid: string;
    faded: string;
  };
  /** WARNING - The prize tiers have different ball matching requirement depending on PREMIUM vs LITE */
  ballMatches: {
    indicator: ReactNode;
    copy: string;
  };
  hasPrizePageLink?: boolean;
  alternativePrizeInfoHeading?: string;
  alternativePrizeInfoText?: string;
  useGenericImage?: boolean;
  modalHeading?: string;
  modalText?: string;
}

const poorlyTypedPrizeTiers = {
  'DAYMAKER+ prize': {
    key: 'DAYMAKERboost',
    color: '#FF399E',
    title: 'DAYMAKER',
    // The non-breaking space after boost is needed to allow the line-break to be hidden for certain screen sizes
    titleWithSuffix: (
      <>
        DAYMAKER<em>boost&nbsp;</em>
        <br />
        prizes
      </>
    ),
    subtitle: 'boost',
    badge: `${ASSET_PREFIX}/images/prizes/PrizeValueDropdownSelector/daymaker-boost.png`,
    examplePrizes: 'worth up to £100k',
    value: 100000,
    poundSigns: {
      solid: '£££££',
      faded: '',
    },
    ballMatches: {
      indicator: (
        <BallMatchIndicator
          mainNumberMatches={4}
          bonusBallMatch
          bonusBallColor="transparent"
          bonusBallOutlineColor="#FB55B0"
        />
      ),
      copy: 'Match 4 + bonus',
    },
    hasPrizePageLink: true,
  },
  'DAYMAKER prize': {
    key: 'DAYMAKER',
    color: '#00C4A7',
    title: 'DAYMAKER',
    titleWithSuffix: 'DAYMAKER prizes',
    badge: `${ASSET_PREFIX}/images/prizes/PrizeValueDropdownSelector/daymaker.png`,
    examplePrizes: 'worth up to £25k',
    value: 25000,
    poundSigns: {
      solid: '££££',
      faded: '',
    },
    ballMatches: {
      indicator: (
        <BallMatchIndicator
          mainNumberMatches={4}
          bonusBallMatch
          bonusBallColor="transparent"
          bonusBallOutlineColor={colors.primaryBrandGray}
        />
      ),
      copy: 'Match 4 + bonus',
    },
    hasPrizePageLink: true,
  },
  'Diamond prize': {
    key: 'Diamond',
    color: '#8430D6',
    title: 'Diamond',
    titleWithSuffix: 'Diamond prizes',
    badge: `${ASSET_PREFIX}/images/prizes/PrizeValueDropdownSelector/diamond.png`,
    examplePrizes: 'worth up to £10k',
    value: 10000,
    poundSigns: {
      solid: '£££',
      faded: '',
    },
    ballMatches: {
      indicator: <BallMatchIndicator mainNumberMatches={4} />,
      copy: 'Match 4',
    },
    hasPrizePageLink: true,
  },
  'Platinum prize': {
    key: 'Platinum',
    color: '#43BFE5',
    title: 'Platinum',
    titleWithSuffix: 'Platinum prizes',
    badge: `${ASSET_PREFIX}/images/prizes/PrizeValueDropdownSelector/platinum.png`,
    examplePrizes: 'worth up to £2k',
    value: 2000,
    poundSigns: {
      solid: '££',
      faded: '£',
    },
    ballMatches: {
      indicator: (
        <BallMatchIndicator
          mainNumberMatches={3}
          bonusBallMatch
          bonusBallColor="transparent"
          bonusBallOutlineColor={colors.primaryBrandGray}
        />
      ),
      copy: 'Match 3 + bonus',
    },
    hasPrizePageLink: true,
  },
  'Gold prize': {
    key: 'Gold',
    color: '#F7C366',
    title: 'Gold',
    titleWithSuffix: 'Gold prizes',
    badge: `${ASSET_PREFIX}/images/prizes/PrizeValueDropdownSelector/gold.png`,
    examplePrizes: 'worth up to £300',
    value: 300,
    poundSigns: {
      solid: '££',
      faded: '',
    },
    ballMatches: {
      indicator: <BallMatchIndicator mainNumberMatches={3} />,
      copy: 'Match 3',
    },
    hasPrizePageLink: true,
  },
  'Silver prize': {
    key: 'Silver',
    color: '#9D9594',
    title: 'Silver',
    titleWithSuffix: 'Silver prizes',
    badge: `${ASSET_PREFIX}/images/prizes/PrizeValueDropdownSelector/silver.png`,
    examplePrizes: 'worth up to £50',
    value: 50,
    poundSigns: {
      solid: '£',
      faded: '£',
    },
    ballMatches: {
      indicator: (
        <BallMatchIndicator
          mainNumberMatches={2}
          bonusBallMatch
          bonusBallColor="transparent"
          bonusBallOutlineColor={colors.primaryBrandGray}
        />
      ),
      copy: 'Match 2 + bonus',
    },
    hasPrizePageLink: true,
  },
  'Bronze prize': {
    key: 'Bronze',
    color: '#F99261',
    title: 'Bronze',
    titleWithSuffix: 'Bronze prizes',
    badge: `${ASSET_PREFIX}/images/prizes/PrizeValueDropdownSelector/bronze.png`,
    examplePrizes: 'worth up to £25',
    value: 25,
    poundSigns: {
      solid: '£',
      faded: '',
    },
    ballMatches: {
      indicator: <BallMatchIndicator mainNumberMatches={2} />,
      copy: 'Match 2',
    },
    hasPrizePageLink: true,
  },
};
// The current combination of NextJS and TypeScript doesn't support using both as const and satisfies, so we have to write this extra line
export type TPrizeTiersByKey = keyof typeof poorlyTypedPrizeTiers;
export const PRIZE_TIERS = poorlyTypedPrizeTiers as Record<
  TPrizeTiersByKey,
  IPrizeTier
>;

const poorlyTypedAdditionalPrizeTiers = {
  DAYMADE_CREDIT: {
    key: 'UNUSEDKEY1',
    color: 'rgba(0,0,0,0)',
    title: 'TEMP',
    titleWithSuffix: 'DAYMADE credit',
    subtitle: 'TEMP',
    badge: `${ASSET_PREFIX}/images/prizes/PrizeValueDropdownSelector/dm-credit.png`,
    examplePrizes: 'TEMP',
    value: 0,
    poundSigns: {
      solid: '£',
      faded: '',
    },
    ballMatches: {
      indicator: <></>,
      copy: '',
    },
    alternativePrizeInfoHeading: 'DAYMADE',
    alternativePrizeInfoText: '£5 credit',
    useGenericImage: true,
    modalHeading: '£5 DAYMADE credit',
    modalText:
      'Win £5 in DAYMADE credit to spend on extra draw entries & tokens.',
  },
  DAYMADE_PREMIUM_ENTRY: {
    key: 'UNUSEDKEY2',
    color: 'rgba(0,0,0,0)',
    title: 'TEMP',
    titleWithSuffix: 'Premium entry',
    subtitle: 'TEMP',
    badge: `${ASSET_PREFIX}/images/prizes/PrizeValueDropdownSelector/dm-premium-entry.png`,
    examplePrizes: 'TEMP',
    value: 0,
    poundSigns: {
      solid: '£',
      faded: '',
    },
    ballMatches: {
      indicator: <></>,
      copy: '',
    },
    alternativePrizeInfoHeading: 'Premium draw',
    alternativePrizeInfoText: '1 entry',
    useGenericImage: true,
    modalHeading: '1 Premium draw entry',
    modalText:
      'Win an extra Premium entry into our Tuesday or Friday weekly draw.',
  },
  DAYMADE_TOKEN: {
    key: 'UNUSEDKEY3',
    color: 'rgba(0,0,0,0)',
    title: 'TEMP',
    titleWithSuffix: 'DAYMADE token',
    subtitle: 'TEMP',
    badge: `${ASSET_PREFIX}/images/prizes/PrizeValueDropdownSelector/dm-token.png`,
    examplePrizes: 'TEMP',
    value: 0,
    poundSigns: {
      solid: '£',
      faded: '',
    },
    ballMatches: {
      indicator: <></>,
      copy: '',
    },
    alternativePrizeInfoHeading: 'DAYMADE',
    alternativePrizeInfoText: '1 token',
    useGenericImage: true,
    modalHeading: 'DAYMADE tokens',
    modalText:
      'Collect and exchange tokens for prizes, free entries, planting trees and exclusive bonus games in Token Town.',
  },
};
// The current combination of NextJS and TypeScript doesn't support using both as const and satisfies, so we have to write this extra line
export const ADDITIONAL_PRIZE_TIERS = poorlyTypedAdditionalPrizeTiers as Record<
  keyof typeof poorlyTypedAdditionalPrizeTiers,
  IPrizeTier
>;
const prizeTiersByKey = Object.values(PRIZE_TIERS).reduce(
  (acc, x) => {
    acc[x.key] = x;
    return acc;
  },
  {} as Record<string, IPrizeTier>,
);

export const getPrizeTierByKey = (prizeTierName: string): IPrizeTier | null => {
  const tier = prizeTiersByKey[prizeTierName as keyof typeof PRIZE_TIERS];
  return tier ?? null;
};

export const getPrizeTierByValue = (
  prizeValue: number,
): IPrizeTier | undefined => {
  return Object.values(PRIZE_TIERS).find(
    (prizeInfo) => prizeInfo.value === prizeValue,
  );
};
